import React, {
	useMemo, useRef, useState, useEffect, forwardRef,
} from 'react';
import { useApolloClient } from '@apollo/client';
import debounce from 'debounce-promise';
import { OverlayTriggerHandle } from 'rsuite/esm/internals/Overlay/OverlayTrigger';

import {
	ActiveFilter, handleErrorNotification, SuperGridFilters, useUrlParams,
} from '@aleph/shared-components';

import {
	getFilterConfig,
	LEVEL_NAME_FILTER,
	LEVEL_ID_FILTER,
	syncFilterOrder,
} from 'wb-spotify/app/routes/superGrid/actionRow/filters/utils';
import CampaignOptionsQuery from 'wb-spotify/app/routes/superGrid/actionRow/filters/api/campaignOptions.graphql';
import GroupOptionsQuery from 'wb-spotify/app/routes/superGrid/actionRow/filters/api/groupOptions.graphql';
import AdOptionsQuery from 'wb-spotify/app/routes/superGrid/actionRow/filters/api/adOptions.graphql';
import AdAccountOptionsQuery from 'wb-spotify/app/routes/superGrid/actionRow/filters/api/adAccountOptions.graphql';
import { motion } from 'framer-motion';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { arraysHaveSameElements } from 'wb-spotify/app/common/utils';

const FILTER_OPTION_QUERIES = {
	[SpotifyLevel.CAMPAIGN]:   CampaignOptionsQuery,
	[SpotifyLevel.GROUP]:      GroupOptionsQuery,
	[SpotifyLevel.AD]:         AdOptionsQuery,
	[SpotifyLevel.AD_ACCOUNT]: AdAccountOptionsQuery,
};

const SpotifyFiltersContainer = forwardRef<HTMLDivElement, {
	filtersOpen: boolean;
	setFiltersOpen:(open: boolean) => void;
	filterButtonPos?: any;
}>(({
			filtersOpen,
			setFiltersOpen,
			filterButtonPos,
		}, ref) => {
			const filterPopoverRef = useRef<OverlayTriggerHandle | null>(null);
			const navigate = useNavigate();

			const { params, setUrlParam } = useUrlParams({ navigate, useParams });
			const client = useApolloClient();

			const [filterOrder, setFilterOrder] = useState(() => Object.keys(params.filters ?? {}));

			/**
			 * Sync filterOrder with params.filters when filters change externally.
			 */
			useEffect(() => {
				const currentFilterKeys = Object.keys(params.filters ?? {});

				if (currentFilterKeys.length === 0) {
					if (filterOrder.length > 0) {
						setFilterOrder([]);
					}

					return;
				}

				if (!arraysHaveSameElements(currentFilterKeys, filterOrder)) {
					setFilterOrder(syncFilterOrder(currentFilterKeys, filterOrder));
				}
			}, [params.filters, filterOrder]);

			const getCurrentFilters = (filterValue: string) => params.filters?.[filterValue]?.[0]?.value ?? [];

			const getEntityOptions = async (filterName: string, search: string, level: SpotifyLevel) => {
				try {
					const { data } = await client.query({
						query:     FILTER_OPTION_QUERIES[level],
						variables: {
							filters: {
								...params?.filters,
								[LEVEL_ID_FILTER[level]]: undefined,
							},
							query: `${LEVEL_NAME_FILTER[level]} LIKE '%${search}%' OR ${LEVEL_ID_FILTER[level]} LIKE '%${search}%'`,
						},
						fetchPolicy: 'network-only',
					});

					if (level === SpotifyLevel.AD_ACCOUNT) {
						return { items: data?.options?.items?.map((item) => ({ ...item?.adAccount, value: item.clientId })) ?? [] };
					}

					return data?.options ?? [];
				} catch (e) {
					handleErrorNotification(e);

					return { items: [] };
				}
			};

			const debouncedGetEntityOptions = useMemo(
				() => debounce(getEntityOptions, 300),
				[JSON.stringify(params?.filters ?? {})],
			);

			const closeFilter = () => {
				if (!filtersOpen) {
					setFiltersOpen(false);
				}
			};

			const setFilters = (filterName: string, filterValue: string[]) => {
				const isRemoving = filterValue?.length === 0;
				const isAdding = filterValue?.length > 0 && !params?.filters?.[filterName];

				setFilterOrder(curr => {
					if (isRemoving) {
						const newOrder = curr.filter(key => key !== filterName);

						// Close filter bar when no filters are selected
						if (newOrder.length === 0) {
							setFiltersOpen(false);
						}

						return newOrder;
					}

					if (isAdding) {
						return [...curr, filterName];
					}

					return curr;
				});

				setUrlParam('filters', {
					...params?.filters,
					[filterName]: filterValue?.length > 0 ? [{ value: filterValue }] : undefined,
				});

				filterPopoverRef?.current?.close();
			};

			const FILTERS_CONFIG = getFilterConfig(
				setFilters,
				getCurrentFilters,
				debouncedGetEntityOptions,
			);

			const activeFilters = (
				<div
					className='flex gap-[8px]'
				>
					{filterOrder.filter(filterKey => params.filters?.[filterKey]).map((filterKey) => {
						const filter = FILTERS_CONFIG.find(f => f.value === filterKey);

						if (!filter) return null;

						return (
							<ActiveFilter
								key={filterKey}
								filterPopoverRef={filterPopoverRef}
								className='rounded-full bg-white text-black hover:bg-gray
							text-[11px] font-semibold'
								urlParams={params}
								filter={filter.value}
								unsetFilter={() => setFilters(filter.value, [])}
								filterComponent={(
									<filter.component
										activeFilters={getCurrentFilters(filter.value)}
										{...filter.componentProps}
									/>
								)}
								filterLabel={(
									<div className="uppercase">
										{filter.label}
									</div>
								)}
								showCount={true}
							/>
						);
					})}
				</div>
			);

			return (
				<motion.div
					ref={ref}
					transition={{ duration: 0.1 }}
					initial={{ height: 0 }}
					animate={{ height: filtersOpen ? 40 : 0 }}
					exit={{ height: 0 }}
				>
					{
						filtersOpen
							? (
								<SuperGridFilters
									openMenu={filtersOpen ? 'filters' : null}
									getCurrentFilters={getCurrentFilters}
									filtersConfiguration={FILTERS_CONFIG}
									ActiveFilters={activeFilters}
									closeFilter={closeFilter}
									filterButtonPos={filterButtonPos}
								/>
							)
							: null
					}
				</motion.div>
			);
		});

export default SpotifyFiltersContainer;
